import { WorkItemStatus } from '@/apps/dispatcher/types/enums';
import { IWorkItem } from '@/apps/dispatcher/types/IWorkItem';
import { normalizeChangesWithNestedObjects } from '@/utils/utils';

export type WorkItemId = NonNullable<IWorkItem['id']>;

export interface WorkItemListScope {
  status: WorkItemStatus;
  contractIds?: number[];
  from?: string;
  to?: string;
}

export interface ActiveWorkItemFetch {
  requestId: number;
  scope: WorkItemListScope;
  touchedIds: WorkItemId[];
}

export interface VersionedWorkItemChange {
  lastModified: string;
  value: unknown;
}

export interface ActiveWorkItemMutation {
  requestId: number;
  changesAfterStart: Record<string, VersionedWorkItemChange>;
}

export interface WorkItemReconciliationState {
  workItems: IWorkItem[];
  activeWorkItemId: IWorkItem['id'] | null;
  activeWorkItem: IWorkItem | null;
  activeFetches: Partial<Record<WorkItemStatus, ActiveWorkItemFetch>>;
  loadedScopes: Partial<Record<WorkItemStatus, WorkItemListScope>>;
  activeMutations: Record<string, ActiveWorkItemMutation>;
}

export const workItemIdKey = (id: WorkItemId): string => String(id);

export const deduplicateWorkItems = (workItems: IWorkItem[]): IWorkItem[] => {
  const seen = new Set<string>();

  return workItems.filter((workItem) => {
    if (workItem.id === undefined) {
      return false;
    }

    const key = workItemIdKey(workItem.id);
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

export const isNewerWorkItemVersion = (
  candidate: string | undefined,
  baseline: string | undefined
): boolean => {
  const candidateTime = candidate ? Date.parse(candidate) : Number.NaN;
  const baselineTime = baseline ? Date.parse(baseline) : Number.NaN;
  return Number.isFinite(candidateTime) && Number.isFinite(baselineTime)
    ? candidateTime > baselineTime
    : false;
};

const uniqueIds = (ids?: number[]): number[] | undefined => {
  if (!ids?.length) {
    return undefined;
  }

  return [...new Set(ids)].sort((left, right) => left - right);
};

export const createWorkItemListScope = (
  status: WorkItemStatus,
  contractIds?: number[],
  from?: string,
  to?: string
): WorkItemListScope => ({
  status,
  contractIds: uniqueIds(contractIds),
  from,
  to,
});

export const isCompleteWorkItem = (
  candidate: Partial<IWorkItem> | null | undefined
): candidate is IWorkItem & { id: WorkItemId } =>
  candidate !== null &&
  candidate !== undefined &&
  (typeof candidate.id === 'number' || typeof candidate.id === 'string') &&
  typeof candidate.contractId === 'number' &&
  typeof candidate.type === 'number' &&
  typeof candidate.status === 'number' &&
  typeof candidate.source === 'number' &&
  typeof candidate.creationType === 'number' &&
  typeof candidate.created === 'string' &&
  typeof candidate.lastModified === 'string';

export const workItemFromCreateSignal = (signal: {
  id: WorkItemId;
  status: WorkItemStatus;
  contract: number;
  changes?: Record<string, unknown>;
}): Partial<IWorkItem> => ({
  ...normalizeChangesWithNestedObjects(signal.changes ?? {}),
  id: signal.id,
  status: signal.status,
  contractId: signal.contract,
});

export const workItemMatchesScope = (
  workItem: IWorkItem,
  scope: WorkItemListScope
): boolean => {
  if (workItem.status !== scope.status) {
    return false;
  }

  if (
    scope.contractIds?.length &&
    (workItem.contractId === undefined ||
      !scope.contractIds.includes(workItem.contractId))
  ) {
    return false;
  }

  if (!scope.from && !scope.to) {
    return true;
  }

  const operationDate = workItem.schedule?.operationDate;
  return Boolean(
    operationDate &&
    (!scope.from || operationDate >= scope.from) &&
    (!scope.to || operationDate <= scope.to)
  );
};

export const signalMatchesScope = (
  status: WorkItemStatus | undefined,
  contractId: number | undefined,
  scopes: Partial<Record<WorkItemStatus, WorkItemListScope>>
): boolean => {
  if (status !== WorkItemStatus.Done && status !== WorkItemStatus.Archive) {
    return false;
  }

  const scope = scopes[status];
  return Boolean(
    scope &&
    (!scope.contractIds?.length ||
      (contractId !== undefined && scope.contractIds.includes(contractId)))
  );
};
